import React, { useState, useContext } from "react";
import { NowPlayingContext } from "./NowPlayingProvider";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { IconButton } from '@mui/material';
import CircularProgress from "@mui/material/CircularProgress";
import LooksOneSharpIcon from "@mui/icons-material/LooksOneSharp";
import LooksTwoSharpIcon from "@mui/icons-material/LooksTwoSharp";
import { PiSoundcloudLogoDuotone } from "react-icons/pi";
import moment from "moment";
import HomeTileCard from "./HomeTileCard";

const CAROUSEL_LIMIT = 8;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

const SongHistory = ({ compact = false, carousel = false }) => {
  const { station, songHistory, songHistoryStation } = useContext(NowPlayingContext);

  const loading = songHistory.length === 0 || songHistoryStation !== station;
  const [open, setOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");

  const handleOpen = (image) => {
    setOpen(true);
    setModalImage(image);
  };

  const handleClose = () => setOpen(false);

  const buttonColor = station === "wssr" ? "primary" : "secondary";

  const deckButton = loading ? (
    <Box display="flex" alignItems="center" sx={{ cursor: "none" }}>
      <CircularProgress size={20} color={buttonColor} sx={{ marginLeft: 1 }} />
    </Box>
  ) : (
    <Button size="small" color={buttonColor} endIcon={station === "wssr" ? <LooksOneSharpIcon /> : <LooksTwoSharpIcon />}>
      Deck
    </Button>
  );

  return (
    <div>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Just Missed</Typography>
        {deckButton}
      </Box>

      {carousel ? (
        <Box sx={{ mx: { xs: -2, md: 0 }, px: { xs: 2, md: 0 } }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {loading ? (
              Array.from(new Array(CAROUSEL_LIMIT)).map((_, index) => (
                <Box key={index} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
                  <HomeTileCard loading />
                </Box>
              ))
            ) : songHistory.slice(0, CAROUSEL_LIMIT).map((entry, index) => {
              const { song } = entry;
              const subtitle = song.text === ' - Live Broadcast' ? entry.streamer : song.artist || '';

              return (
                <Box
                  key={entry.sh_id || index}
                  className="fade-in-track"
                  sx={{ flexShrink: 0, scrollSnapAlign: 'start', cursor: 'pointer' }}
                  onClick={() => handleOpen(song.art)}
                >
                  <HomeTileCard imageSrc={song.art} title={song.title || 'Unknown Title'} subtitle={subtitle} />
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : compact ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {loading ? (
            Array.from(new Array(10)).map((_, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Skeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: 1 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="60%" height={16} />
                </Box>
              </Box>
            ))
          ) : songHistory.map((entry, index) => {
            const { song } = entry;
            const playedAt = moment.unix(entry.played_at).fromNow();

            return (
              <Box key={entry.sh_id || index} className="fade-in-track" sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Box sx={{ width: 48, height: 48, position: 'relative', flexShrink: 0 }}>
                  <img
                    src={song.art}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer', borderRadius: '4px' }}
                    alt={song.title || 'Song Art'}
                    onClick={() => handleOpen(song.art)}
                  />

                  {song?.custom_fields?.elink && (
                    <IconButton
                      component="a"
                      href={song?.custom_fields?.elink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: -2,
                        padding: '3px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: '#FF991C',
                        borderRadius: '0%',
                        fontSize: '0.7rem',
                        '& svg': { fontSize: '0.8rem' },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        },
                      }}
                    >
                      <PiSoundcloudLogoDuotone />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{song.title || 'Unknown Title'}</Typography>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block' }} noWrap>
                    {song.text === ' - Live Broadcast' ? entry.streamer : song.artist || ''}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>{playedAt}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={3} sx={{ marginBottom: "8rem" }}>
            {loading ? (
              Array.from(new Array(5)).map((_, index) => (
                <Grid item xs={12} key={index}>
                  <Grid container spacing={2}>
                    <Grid item xs={5} md={4}>
                      <Box sx={{ width: '100%', paddingTop: '100%', position: 'relative' }}>
                        <Skeleton variant="rectangular" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 1 }} />
                      </Box>
                    </Grid>
                    <Grid item xs={7} md={8} display="flex" justifyContent="center" flexDirection="column">
                      <Skeleton variant="text" width="80%" height={24} />
                      <Skeleton variant="text" width="60%" height={20} />
                      <Skeleton variant="text" width="40%" height={18} />
                    </Grid>
                  </Grid>
                </Grid>
              ))
            ) : songHistory.map((entry, index) => {
              const { song } = entry;
              const playedAt = moment.unix(entry.played_at).fromNow();

              return (
                <Grid item xs={12} key={entry.sh_id || index}>
                  <Grid container spacing={2}>
                    <Grid item xs={5} md={4}>
                      <Box sx={{ width: "100%", paddingTop: "100%", position: "relative" }}>
                        <img
                          src={song.art}
                          style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0, cursor: "pointer", borderRadius: '4px' }}
                          alt={song.title || "Song Art"}
                          onClick={() => handleOpen(song.art)}
                        />

                        {song?.custom_fields?.elink && (
                          <IconButton
                            component="a"
                            href={song?.custom_fields?.elink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            sx={{
                              clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
                              position: 'absolute',
                              bottom: 2,
                              right: 2,
                              padding: '8px',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: '#FF991C',
                              borderRadius: '0%',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                padding: '12px',
                                clipPath: 'polygon(0 20%, 80% 0, 100% 80%, 20% 100%)',
                              }
                            }}
                          >
                            <PiSoundcloudLogoDuotone />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={7} md={8} display="flex" justifyContent="center" flexDirection="column">
                      <Typography variant="subtitle1">{song.title || "Unknown Title"}</Typography>
                      <Typography variant="subtitle1" sx={{ color: "#999" }}>
                        {song.text === " - Live Broadcast"
                          ? entry.streamer
                          : song.artist || ""}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "#999" }}>{playedAt}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <img src={modalImage} alt="Expanded Art" style={{ maxWidth: "90vw", maxHeight: "90vh" }} />
        </Box>
      </Modal>
    </div>
  );
};

export default SongHistory;
